import React from 'react';
import TableToolbar from '@rio-cloud/rio-uikit/TableToolbar';
import { CustomFormattedMessage, useCustomIntl } from '../../../../i18n/i18n';
import { useAppDispatch, useAppSelector } from '../../../../state/hooks';
import {
  getBookingsStatusFilter,
  getFromFilter,
  getPeriodFilter,
  getToFilter,
  resetBookingsFilter,
  setBookingsStatus,
  setFrom,
  setPeriod,
  setTo,
} from '../../../../state/bookings/bookingsFilterSlice';
import {
  parsePeriod,
  usePeriodSelectOptions,
} from '../../../../usePeriodSelectOptions';
import Select from '@rio-cloud/rio-uikit/Select';
import Button from '@rio-cloud/rio-uikit/Button';
import { useStatusOptions } from '../../../../state/bookings/useBookings';
import Multiselect from '@rio-cloud/rio-uikit/Multiselect';
import { BookingStatus } from '../../../../codegen/bookings';
import moment from 'moment/moment';
import { UserManualLink } from '../../../../components/dialog/userManualDialog/UserManualLink';
import { userManualDialogChapters } from '../../../../state/dialogs/chapters';
import { DateFilterPicker } from '../../../../components/DateFilterPicker';

export const BookingsTableToolbar: React.FC = () => {
  const dispatch = useAppDispatch();
  const intl = useCustomIntl();
  const { bookingFilterPeriods } = usePeriodSelectOptions();

  const from = moment(useAppSelector(getFromFilter));
  const to = moment(useAppSelector(getToFilter));
  const period = useAppSelector(getPeriodFilter);
  const status = useAppSelector(getBookingsStatusFilter);

  const statusOptions = useStatusOptions().map((statusOption) => {
    return {
      ...statusOption,
      selected: status.includes(statusOption.id),
    };
  });

  return (
    <TableToolbar>
      <div
        className="table-toolbar-container"
        data-testid={'bookings-table-toolbar'}
      >
        <div className="table-toolbar-group-left gap-15 margin-bottom-15">
          <div className="btn-toolbar table-btn-toolbar">
            <DateFilterPicker
              labelId={'common.startDate'}
              value={from}
              onChange={(d) => dispatch(setFrom(d))}
              blockFutureDates={false}
              isDateBlocked={(d) => d.isAfter(to, 'day')}
              errorMessageId={(d) =>
                d.isAfter(to, 'day') && 'common.timerange.beforeEndDate'
              }
              data-testid={'bookings-table-start-date-picker'}
            />
          </div>
          <div className="btn-toolbar table-btn-toolbar">
            <DateFilterPicker
              labelId={'common.endDate'}
              value={to}
              onChange={(d) => dispatch(setTo(d))}
              blockFutureDates={false}
              isDateBlocked={(d) => d.isBefore(from, 'day')}
              errorMessageId={(d) =>
                d.isBefore(from, 'day') && 'common.timerange.afterStartDate'
              }
              data-testid={'bookings-table-end-date-picker'}
            />
          </div>
          <div className="btn-toolbar table-btn-toolbar">
            <div data-testid={'bookings-table-period-selector'}>
              <label>
                <CustomFormattedMessage id={'common.period'} />
              </label>
              <Select
                useClear
                className={'width-200'}
                id={'periodSelector'}
                placeholder={
                  <CustomFormattedMessage id={'common.selectPeriod'} />
                }
                options={bookingFilterPeriods}
                onChange={(option) => {
                  const value = option ? parsePeriod(option.id) : undefined;
                  dispatch(setPeriod(value));
                }}
                value={period ? [period.toString()] : []}
              />
            </div>
          </div>
          <div className="btn-toolbar table-btn-toolbar">
            <div data-testid="bookings-table-status-filter">
              <label>
                <CustomFormattedMessage id={'common.status'} />
              </label>
              <Multiselect
                className={'width-200'}
                placeholder={intl.formatMessage({
                  id: 'bookings.bookingsTabel.toolbar.statusFilterPlaceholder',
                })}
                id={'statusSelector'}
                options={statusOptions}
                onChange={(selected: string[]) => {
                  dispatch(setBookingsStatus(selected as BookingStatus[]));
                }}
              />
            </div>
            <div className={'align-content-end'}>
              <UserManualLink
                chapter={userManualDialogChapters.bookingsBookingOverview}
                dataTestId="user-manual-link-bookings-table"
                className="margin-10"
              />
            </div>
          </div>
        </div>
        <div className="table-toolbar-group-right">
          <div className="table-toolbar-column">
            <div className="btn-toolbar table-btn-toolbar">
              <Button
                bsStyle={'primary'}
                onClick={() => {
                  dispatch(resetBookingsFilter());
                }}
              >
                <span className={'rioglyph rioglyph-revert'} />
                <CustomFormattedMessage
                  id={'transactions.transactionsTable.toolbar.resetFilters'}
                />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </TableToolbar>
  );
};
